import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { useLanguage } from "@/hooks/useLanguage";
import { type LucideIcon } from "lucide-react";

interface FloatingIcon {
  icon: LucideIcon;
  color: string;
  position: string;
  duration: string;
  delay: string;
}

interface IndustryHeroI18nProps {
  translationPrefix: string;
  heroImage: string;
  floatingIcons: FloatingIcon[];
}

const IndustryHeroI18n = ({ translationPrefix, heroImage, floatingIcons }: IndustryHeroI18nProps) => {
  const { t, localizePath } = useLanguage();
  const p = `${translationPrefix}.hero`;

  return (
    <section className="relative pt-36 md:pt-48 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/30" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-2">
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localizePath("/")} className="text-muted-foreground hover:text-primary">
                      {t(`${p}.home`)}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-muted-foreground">{t(`${p}.industries`)}</span>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-chathero-orange font-medium">
                    {t(`${p}.breadcrumbCurrent`)}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary leading-tight mb-6 animate-fade-in">
              {t(`${p}.title`)}
            </h1>

            <p className="text-xl md:text-2xl text-chathero-orange font-semibold mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              {t(`${p}.subtitle`)}
            </p>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {t(`${p}.description`)}
            </p>

            <div className="flex flex-wrap justify-center lg:justify-start gap-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <Button size="lg" className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90 text-base px-8" asChild>
                <Link to={localizePath("/kontakt")}>{t(`${p}.ctaPrimary`)}</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base px-8" asChild>
                <a href="https://app.chathero.ai/register/essential" target="_blank" rel="noopener noreferrer">{t(`${p}.ctaSecondary`)}</a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-1 hidden lg:flex justify-center lg:justify-end animate-scale-in" style={{ animationDelay: "0.3s" }}>
            <div className="relative w-full max-w-md lg:max-w-none">
              <div className="absolute -top-8 -right-8 w-[350px] h-[350px] rounded-full bg-gradient-to-br from-chathero-mint to-chathero-mint/40 blur-sm" />

              <div className="relative z-10">
                <img
                  src={heroImage}
                  alt={t(`${p}.title`)}
                  className="w-full aspect-[4/5] object-cover rounded-3xl shadow-2xl"
                  width="400"
                  height="500"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>

              {floatingIcons.map((fi, i) => (
                <div
                  key={i}
                  className={`absolute ${fi.position} w-14 h-14 rounded-2xl bg-background shadow-xl flex items-center justify-center animate-bounce z-20`}
                  style={{ animationDuration: fi.duration, animationDelay: fi.delay }}
                >
                  <fi.icon className={`w-7 h-7 ${fi.color}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryHeroI18n;
